import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout';
import { Plus, Pencil, Trash2, ArrowRight, Users as UsersIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';
import { parseApiError } from '../lib/apiErrors';

function GroupFormModal({ title, initial, onClose, onSubmit }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSubmit({ name: name.trim(), description: description.trim() || null });
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        setError(parseApiError(err, 'A group with this name already exists').message);
      } else {
        toast.error(parseApiError(err, 'Failed to save group').message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              placeholder="e.g. Class 10 CBSE Batch A"
              data-testid="group-name-input"
            />
            {error && <p className="mt-1 text-sm text-rose-500" data-testid="group-name-error">{error}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              placeholder="Optional"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
              data-testid="group-form-submit"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | group object being renamed

  const load = useCallback(() => {
    setLoading(true);
    adminApi.listGroups()
      .then(({ data }) => setGroups(data))
      .catch(() => toast.error('Failed to load groups'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (payload) => {
    const { data } = await adminApi.createGroup(payload);
    setGroups((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    toast.success('Group created');
  };

  const handleRename = async (group, payload) => {
    const { data } = await adminApi.updateGroup(group.id, payload);
    setGroups((prev) => prev.map((g) => (g.id === group.id ? data : g)).sort((a, b) => a.name.localeCompare(b.name)));
    toast.success('Group updated');
  };

  const handleDelete = async (group) => {
    if (!window.confirm(
      `Delete "${group.name}"? Its ${group.member_count} membership(s) will be removed. ` +
      'Students, their exams, and their history are not affected.'
    )) return;
    try {
      await adminApi.deleteGroup(group.id);
      setGroups((prev) => prev.filter((g) => g.id !== group.id));
      toast.success('Group deleted');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete group').message);
    }
  };

  return (
    <DashboardLayout title="Groups">
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Groups</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Organize students for exam targeting
            </p>
          </div>
          <button
            onClick={() => setModal('create')}
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-light text-white shadow-sm rounded-md px-4 py-2.5 transition-colors duration-200 font-medium"
            data-testid="create-group-btn"
          >
            <Plus size={20} />
            Create Group
          </button>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          {loading ? (
            <div className="animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/6"></div>
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">No groups yet. Create one to get started.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Name</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Description</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Members</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Created</th>
                  <th className="px-5 py-3 text-right font-medium text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {groups.map((g) => (
                  <tr key={g.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-testid={`group-row-${g.id}`}>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => navigate(`/admin/groups/${g.id}`)}
                        className="font-medium text-slate-900 dark:text-white hover:text-primary dark:hover:text-accent-cool text-left"
                        data-testid={`open-group-${g.id}`}
                      >
                        {g.name}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400 max-w-xs truncate">{g.description || '—'}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                        <UsersIcon size={14} /> {g.member_count}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {g.created_at ? new Date(g.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/admin/groups/${g.id}`)}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 transition-colors"
                        >
                          Open <ArrowRight size={13} />
                        </button>
                        <button
                          onClick={() => setModal(g)}
                          className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
                          title="Rename"
                          data-testid={`rename-group-${g.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(g)}
                          className="p-2 rounded-md hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors text-rose-500"
                          title="Delete"
                          data-testid={`delete-group-${g.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal === 'create' && (
        <GroupFormModal title="Create Group" onClose={() => setModal(null)} onSubmit={handleCreate} />
      )}
      {modal && modal !== 'create' && (
        <GroupFormModal
          title="Rename Group"
          initial={modal}
          onClose={() => setModal(null)}
          onSubmit={(payload) => handleRename(modal, payload)}
        />
      )}
    </DashboardLayout>
  );
}
